import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import "../../assets/custom/css/cti.css";
import NavBar from "./NavBar";
import ConferenceScreen from "./screens/ConferenceScreen";
import IdleScreen from "./screens/IdleScreen";
import OnCallScreen from "./screens/OnCallScreen";
import RingingScreen from "./screens/RingingScreen";
import Header from "../../components/partials/dashboard/HeaderStyle/header";
import { Modal } from "react-bootstrap";
import { Form } from "react-bootstrap";
import { Button } from "primereact/button";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { API_AGENT_DASHBOARD_CAMPAIGNS } from "../../service/url";
import { USER_ROLES } from '../../utilities/constant';
import { postApiCall, getApiCall } from "../../service/httpCall";
import { getIdleSeconds } from "./ctiHelpers";
import {
  API_BREAK_LIST,
  USER_BREAKS_EDIT,
  USER_BREAKS_CREATE,
  API_AGENT_DASHBOARD_CALLS,
  API_AGENT_IDLE_STATS,
  API_AGENT_ANNOUNCEMENT_LIST,
  API_CALLS_CREATE,
} from "../../service/url";
import { toast } from "react-toastify";
import { BreakContext } from "../../router/context/BreakContext";
import { useCallStore } from "../../router/zustand/callStore";

const durationToSeconds = (duration) => {
  const minutes = parseInt(duration, 10);
  return minutes * 60;
};

function CTI() {
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const handleShow = () => setShowBreakModal(true);
  const handleClose = () => setShowBreakModal(false);
  // const [screen, setScreen] = useState("idle");
  const [theme, setTheme] = useState(
    () => localStorage.getItem("cti-theme") || "light",
  );
  const [activeTab, setActiveTab] = useState("log");
  const [dialNum, setDialNum] = useState("");
  const [holdOn, setHoldOn] = useState(false);
  const [muteOn, setMuteOn] = useState(false);
  const [idleStatus, setIdleStatus] = useState("READY");
  const [idleSeconds, setIdleSeconds] = useState(0);
  const [callSeconds, setCallSeconds] = useState(272);
  const [confSeconds, setConfSeconds] = useState(728);
  const [breaksData, setBreaksData] = useState([]);
  const [userData, setUserData] = useState(null);
  const { breakEndTime, userStatus, startBreak, stopBreak, setSelectedBreakId, selectedBreakId, countdown} = useContext(BreakContext);

  const [loggedUserRole, setLoggedUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentCallsData, setAgentCallsData] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  // const [webrtcStatus, setWebrtcStatus] = useState(null);
  const [idleStartedAt, setIdleStartedAt] = useState(() => new Date().toISOString());
  const [manualScreen, setManualScreen] = useState(null);
  const [telephonyProfileActive, setTelephonyProfileActive] = useState(false)

  const webrtcStatus = useCallStore((state) => state.webrtcPhoneStatus);
  const webrtcIdleTime = useCallStore((state) => state.webrtcPhoneStartTime);
  const idleSessionStartedAt = useCallStore((state) => state.idleSessionStartedAt);

  const parsedStatus = useMemo(() => {
    if (!webrtcStatus) {
      return {
        success: false,
        code: "DISCONNECTED",
        message: "Dialer is Disconnected",
      };
    }
  
    const status = webrtcStatus.toLowerCase();
  
    // ✅ SUCCESS CASE (only 200 / connected)
    if (status === "connected") {
      return {
        success: true,
        code: "READY",
        message: "Dialer connected",
      };
    }
  
    // ✅ AUTHENTICATING (401 first case)
    if (status.includes("authenticating")) {
      return {
        success: false,
        code: "AUTH",
        message: "Authenticating...",
      };
    }
  
    // ✅ GENERIC "code - message" fallback
    const match = webrtcStatus.match(/^(\d+)\s*-\s*(.*)$/);
  
    if (match) {
      return {
        success: false,
        code: match[1],
        message: match[2],
      };
    }
  
    // ✅ FALLBACK (plain message)
    return {
      success: false,
      code: "ERROR",
      message: webrtcStatus,
    };
  }, [webrtcStatus]);

  const callState = useCallStore((state) => state.callState);
  const completedCallToPersist = useCallStore((state) => state.completedCallToPersist);

  useEffect(() => {
  setManualScreen(null);
}, [callState]);

  const screen = useMemo(() => {
    if (manualScreen) return manualScreen;

    switch (callState) {
      case "incoming":
        return "ringing";
      case "oncall":
        return "oncall";
      case "conference":
        return "conference";
      default:
        return "idle";
    }
  }, [callState, manualScreen]);

  useEffect(() => {
    localStorage.setItem("cti-theme", theme);
  }, [theme]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (screen === "oncall") setCallSeconds((value) => value + 1);
      if (screen === "conference") setConfSeconds((value) => value + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [screen]);

  useEffect(() => {
    if (screen === "idle" && parsedStatus?.success) {
      setIdleStartedAt(new Date().toISOString());
      // setIdleSeconds(0);
    }
  }, [screen, parsedStatus]);

  useEffect(() => {
    if (!parsedStatus?.success) {
      setIdleSeconds(0);
      return;
    }

    if (screen !== "idle") {
      setIdleSeconds(0);
      return;
    }

    const startAt = idleSessionStartedAt || webrtcIdleTime;
    if (!startAt) {
      setIdleSeconds(0);
      return;
    }

    setIdleSeconds(getIdleSeconds(startAt));
    const interval = setInterval(() => {
      setIdleSeconds(getIdleSeconds(startAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [idleSessionStartedAt, parsedStatus, screen, webrtcIdleTime]);

  const statusMeta = useMemo(() => {
    if (screen === "ringing") return { text: "RINGING", dotClass: "ring" };
    if (screen === "oncall") return { text: "ON CALL", dotClass: "busy" };
    if (screen === "conference") return { text: "CONF", dotClass: "busy" };
    return { text: "READY", dotClass: "" };
  }, [screen]);

  const fetchUserData = async () => {
    try {
      const user = JSON.parse(await AsyncStorage.getItem("user"));
      const telephonyProfile = user?.agentData?.agentTelephony?.profile
      if (user) {
        setUserData(user);
      }
    } catch (error) {
      console.log("Error getting logged in user data", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (!userData?.id) return;
    useCallStore.getState().setCallCreatedBy(userData.id);
  }, [userData]);

  useEffect(() => {
    if (!completedCallToPersist) return;

    const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

    const createdBy = completedCallToPersist.created_by || userData?.id || null;
    const callerNumber = digitsOnly(completedCallToPersist.caller_number);

    if (!createdBy || !callerNumber) {
      useCallStore.getState().clearCompletedCallToPersist();
      return;
    }

    const payload = {
      did: completedCallToPersist.did || null,
      caller_number: callerNumber,
      answer_status: completedCallToPersist.answer_status || null,
      end_time: completedCallToPersist.end_time || null,
      patch_duration: completedCallToPersist.patch_duration ?? null,
      billing_duration: completedCallToPersist.billing_duration ?? null,
      dtmf: completedCallToPersist.dtmf || null,
      dnd: completedCallToPersist.dnd ?? null,
      call_sid: completedCallToPersist.call_sid || null,
      start_time: completedCallToPersist.start_time || null,
      pulses: completedCallToPersist.pulses ?? null,
      hangup_cause: completedCallToPersist.hangup_cause || null,
      trunk: completedCallToPersist.trunk || null,
      callee_number: completedCallToPersist.callee_number || null,
      bridge_time: completedCallToPersist.bridge_time || null,
      bridge_ring_duration: completedCallToPersist.bridge_ring_duration ?? null,
      recording_file: completedCallToPersist.recording_file || null,
      recording_uploaded: completedCallToPersist.recording_uploaded ?? false,
      caller_profile: completedCallToPersist.caller_profile || null,
      category: completedCallToPersist.category || "cti",
      created_by: createdBy,
    };

    postApiCall(API_CALLS_CREATE, payload)
      .catch((error) => {
        console.error("Failed to save call record", error);
      })
      .finally(() => {
        useCallStore.getState().clearCompletedCallToPersist();
      });
  }, [completedCallToPersist, userData]);

  useEffect(() => {
    getApiCall(`${API_BREAK_LIST}?status=1`, {})
      .then(onListSuccess)
      .catch(onError);
  }, []);

  function onError(err) {
    console.log(err);
  }

  function onListSuccess(resp) {
    setBreaksData(resp);
  }

  useEffect(() => {
      const fetchUserDataAndCampaigns = async () => {
        const rawUser = await AsyncStorage.getItem("user");
    
        if (!rawUser) {
          console.error("User data not found in AsyncStorage");
          return;
        }
    
        let user;
        try {
          user = JSON.parse(rawUser);
        } catch (e) {
          console.error("Invalid JSON in AsyncStorage user:", e);
          return;
        }

        if (user) {
          setUserData(user);
        }

        const telephonyProfile = user?.agentData?.agentTelephony?.profile
        if (telephonyProfile) {
          const isActive = telephonyProfile.some(profile => 
            (profile.type === "Soft Phone" || profile.type === "WEBRTC") &&
            profile.active_profile === true
          );

          setTelephonyProfileActive(isActive);
        }
    
        const role = user?.role;
        setLoggedUserRole(role);
    
        if (role === USER_ROLES.CALL_CENTRE_AGENT) {
          const id = user?.agentData?.id;
          setLoggedUserId(id);
          fetchCampaigns(role, id);
          fetchAgentCallsData(role, id);
        }
      };
    
      fetchUserDataAndCampaigns();
    }, []);

    const fetchCampaigns = async (role, agentId = null) => {
      setLoading(true);
      let url = API_AGENT_DASHBOARD_CAMPAIGNS;
    
      if (role === USER_ROLES.CALL_CENTRE_AGENT && agentId) {
        url += `?agent_id=${agentId}`;
      }
      try {
        const res = await getApiCall(url, {});
        onCampaignListSuccess(res);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchAnnouncements = async () => {
      try {
        const resp = await getApiCall(API_AGENT_ANNOUNCEMENT_LIST, {});
        setAnnouncements(Array.isArray(resp) ? resp : []);
      } catch (error) {
        onError(error);
      }
    };
    
    function onCampaignListSuccess(resp) {
      setCampaigns(resp);
    }

    const fetchAgentCallsData = async (role, agentId = null) => {
      setLoading(true);
      let url = API_AGENT_DASHBOARD_CALLS;
    
      if (role === USER_ROLES.CALL_CENTRE_AGENT && agentId) {
        url = url.replace(":id", agentId);
      }
      try {
        const res = await getApiCall(url, {});
        onAgentCallsSuccess(res);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    };

    function onAgentCallsSuccess(resp) {
      setAgentCallsData(resp);
    }

    useEffect(() => {
      if (!loggedUserRole || !loggedUserId || (loggedUserRole !== USER_ROLES.CALL_CENTRE_AGENT)) return;
      let isMounted = true;
      let timeoutId;
      const poll = async () => {
        if (!isMounted) return;
        await fetchAgentCallsData(loggedUserRole, loggedUserId);
        await fetchCampaigns(loggedUserRole, loggedUserId);
        timeoutId = setTimeout(poll, 6000);
      };
      poll();
      return () => {
        isMounted = false;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, [loggedUserRole, loggedUserId]);

    useEffect(() => {
      if (!loggedUserRole || (loggedUserRole !== USER_ROLES.CALL_CENTRE_AGENT)) return;
      let isMounted = true;
      let timeoutId;
      const poll = async () => {
        if (!isMounted) return;
        await fetchAnnouncements();
        timeoutId = setTimeout(poll, 10000);
      };
      poll();
      return () => {
        isMounted = false;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, [loggedUserRole]);

    useEffect(() => {
      if (telephonyProfileActive) {
        const timeout = setTimeout(() => {
          try {
            require("../../components/partials/dashboard/CallIcon/demo-1");
          } catch (err) {
            console.error("Error loading demo file:", err);
          }
        }, 1000);
        return () => clearTimeout(timeout);
      }
    }, [telephonyProfileActive]);

  const sendAgentIdleStats = useCallback(
    async (toOverride) => {
      if (!parsedStatus?.success) return;
      if (!loggedUserId) return;

      const {
        idleSessionStartedAt,
        webrtcPhoneStartTime,
        incomingReceivedAt,
        idleStatsSentForIncomingAt,
      } = useCallStore.getState();

      if (incomingReceivedAt && idleStatsSentForIncomingAt === incomingReceivedAt) {
        return;
      }

      const from = idleSessionStartedAt || webrtcPhoneStartTime || idleStartedAt;
      const to = toOverride || incomingReceivedAt || new Date().toISOString();

      if (!from) return;

      const payload = {
        from,
        to,
        agent_id: loggedUserId,
      };

      try {
        await postApiCall(API_AGENT_IDLE_STATS, payload);
        if (incomingReceivedAt) {
          useCallStore.getState().markIdleStatsSentForIncomingAt(incomingReceivedAt);
        }
      } catch (error) {
        console.error("Failed to save agent idle stats", error);
      }
    },
    [idleStartedAt, loggedUserId, parsedStatus],
  );

  const handlePlaceCall = useCallback(async () => {
    // await sendAgentIdleStats();
    // setScreen("oncall");
  }, [sendAgentIdleStats]);

  const handleRinging = useCallback((value) => {
    if (value) {
      setDialNum(value);
    }
    // setScreen("ringing");
  }, []);

  const handleAcceptCall = useCallback(async () => {
    const session = useCallStore.getState().session;
    const incomingReceivedAt = useCallStore.getState().incomingReceivedAt;

    if (!session) {
      console.error("No session found");
      return;
    }

    try {
      await session.accept();
      console.log("Call accepted");
      await sendAgentIdleStats(incomingReceivedAt || new Date().toISOString());
    } catch (err) {
      console.error("Error accepting call:", err);
    }
  }, [sendAgentIdleStats]);

const handleRejectCall = useCallback(() => {
  const session = useCallStore.getState().session;

  if (!session) {
    console.log("No session to reject");
    return;
  }

  try {
    useCallStore.getState().markActiveCallDeclinedByAgent();
    session.reject({
      // statusCode: 486,
      statusCode: 603,
    });
    console.log("Call rejected");

    useCallStore.getState().endCall({ resetIdle: false });
  } catch (err) {
    console.error("Error rejecting call:", err);
  }
}, []);

const handleEndCall = useCallback(() => {
  const session = useCallStore.getState().session;

  if (!session) {
    console.log("No session to reject");
    return;
  }

  try {
    session.bye();
    console.log("Call Ended");

    useCallStore.getState().endCall();
  } catch (err) {
    console.error("Error Ended call:", err);
  }
}, []);

  return (
    <>
      <div className="cti-app-shell" data-theme={theme}>
        <NavBar
          screen={screen}
          onScreenChange={setManualScreen}
          statusMeta={statusMeta}
          theme={theme}
          userData={userData}
          agentCallsData={agentCallsData}
          onToggleTheme={() =>
            setTheme((value) => (value === "light" ? "dark" : "light"))
          }
        />

        {screen === "idle" && (
          <IdleScreen
            idleSeconds={idleSeconds}
            idleStatus={idleStatus}
            activeTab={activeTab}
            dialNum={dialNum}
            breakEndTime={breakEndTime}
            countdown={countdown}
            breaksData={breaksData}
            selectedBreakId={selectedBreakId}
            onDial={(value) => setDialNum((current) => current + value)}
            onDialBack={() => setDialNum((current) => current.slice(0, -1))}
            onTabChange={setActiveTab}
            onStatusChange={setIdleStatus}
            onPlaceCall={handlePlaceCall}
            onRinging={handleRinging}
            onSetBreakClick={() => setShowBreakModal(true)}
            onRemoveBreakClick={() => setShowCancelModal(true)}
            userData={userData}
            campaigns={campaigns}
            theme={theme}
            userStatus={userStatus}
            agentCallsData={agentCallsData}
            loggedUserRole={loggedUserRole}
            loggedUserId={loggedUserId}
            idleFrom={idleSessionStartedAt || webrtcIdleTime}
            announcements={announcements}
          />
        )}

        {screen === "ringing" && (
          <RingingScreen 
          onAccept={handleAcceptCall}
          agentCallsData={agentCallsData}
          onReject={handleRejectCall}/>
        )}

        {screen === "oncall" && (
          <OnCallScreen
            callSeconds={callSeconds}
            holdOn={holdOn}
            muteOn={muteOn}
            onToggleHold={() => setHoldOn((value) => !value)}
            onToggleMute={() => setMuteOn((value) => !value)}
            onGoConference={() => setManualScreen("conference")}
            // onEndCall={() => setScreen("idle")}
            campaigns={campaigns}
            agentCallsData={agentCallsData}
            screen={screen}
            onReject={handleEndCall}
          />
        )}

        {screen === "conference" && (
          <ConferenceScreen
            confSeconds={confSeconds}
            // onLeave={() => setScreen("oncall")}
            // onEndAll={() => setScreen("idle")}
            agentCallsData={agentCallsData}
          />
        )}
      </div>
      {/* Break Selection Modal */}
      <Modal show={showBreakModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Choose Break</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="breakSelect">
            <Form.Label>
              Select a break:
              <span style={{ color: "red", margin: "0 4px" }}>*</span>
            </Form.Label>
            <Form.Select
              value={selectedBreakId}
              onChange={(e) => setSelectedBreakId(e.target.value)}
            >
              <option value="">Select Break</option>
              {breaksData.map((brk) => (
                <option key={brk.id} value={brk.id}>
                  {brk.title} - ({brk.duration} Mins)
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            style={{ marginRight: "20px", backgroundColor: "grey" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ backgroundColor: "#1789e5ff" }}
            onClick={() => {
              startBreak(selectedBreakId);
              handleClose();
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Break Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Break</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to cancel the break?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            style={{ marginRight: "20px", backgroundColor: "grey" }}
          >
            No
          </Button>
          <Button
            variant="danger"
              onClick={() => {
                const user = JSON.parse(localStorage.getItem("user"));
                stopBreak(user?.id);
                setShowCancelModal(false)
              }}
              style={{ backgroundColor: "#1789e5ff" }}
          >
            Yes, Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CTI;
